import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { LayoutMode, useLayoutMode } from '../hooks/useMediaQuery';
import { cx } from '../lib/cx';
import { useGhostClickGuard } from './ghostClick';
import { PlusIcon } from './icons';
import { Sheet } from './Sheet';
import styles from './AddPopover.module.css';

interface AddPopoverProps {
  /** Accessible name of the "+" button and of the panel, e.g. "Add check-in". */
  readonly label: string;
  readonly testId: string;
  readonly className?: string;
  /** Controlled open state (optional): lets code open the panel, e.g. from a tile menu. */
  readonly open?: boolean;
  /** Called with the next open state when the "+" button toggles it or the panel closes. */
  readonly onOpenChange?: (open: boolean) => void;
  /** The form; `close` dismisses the panel and returns focus to the "+" button. */
  readonly children: (close: () => void) => ReactNode;
}

const OFFSET_PX = 6;
const EDGE_PX = 8;
const WIDTH_PX = 280;
/** The panel never gets shorter than this when the space below the button is tight; it scrolls instead. */
const MIN_PANEL_HEIGHT_PX = 120;

/** The first control the user is expected to type into. */
function firstField(root: HTMLElement): HTMLElement | null {
  return root.querySelector<HTMLElement>('input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled)');
}

/**
 * The shared "+" popover of non-primary editing pages (ui-spec "Add-only entry points"): a 32px
 * icon button in a card's title row (44px on coarse pointers) that opens a small form. It is
 * anchored below the button with its right edge aligned (flipped above near the viewport
 * bottom), 280px wide capped at the viewport minus 16px, and a bottom sheet below 768px (the
 * soft keyboard would cover an anchored panel). Focus goes to the first field on open; Esc
 * (consumed so neither a picker dialog nor quick-mark mode also reacts) and a press outside
 * close it and focus returns to the button.
 */
export function AddPopover({ label, testId, className, open: controlledOpen, onOpenChange, children }: AddPopoverProps) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = useCallback(
    (next: boolean): void => {
      setInnerOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const [button, setButton] = useState<HTMLButtonElement | null>(null);
  const panelId = useId();
  const narrow = useLayoutMode() === LayoutMode.Narrow;

  // The panel lives in a different tree per layout mode (bottom sheet vs portal), so a switch
  // while open would remount the form and silently drop typed values. The simplest consistent
  // behaviour: a switch (rotation, window resize across 768px) closes the panel deliberately;
  // the user reopens it in the new layout.
  const previousNarrow = useRef(narrow);
  useEffect(() => {
    if (previousNarrow.current === narrow) return;
    previousNarrow.current = narrow;
    setOpen(false);
  }, [narrow, setOpen]);

  const close = useCallback((): void => {
    setOpen(false);
    button?.focus({ preventScroll: true });
  }, [button, setOpen]);

  return (
    <>
      <button
        ref={setButton}
        type="button"
        className={cx(styles.button, className)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        data-testid={testId}
        onClick={() => setOpen(!open)}
      >
        <PlusIcon />
      </button>
      {open ? (
        narrow ? (
          <Sheet label={label} onClose={close}>
            <Panel id={panelId} label={label} testId={testId} onClose={close} anchor={null} sheet>
              {children(close)}
            </Panel>
          </Sheet>
        ) : (
          <Panel id={panelId} label={label} testId={testId} onClose={close} anchor={button}>
            {children(close)}
          </Panel>
        )
      ) : null}
    </>
  );
}

interface PanelProps {
  readonly id: string;
  readonly label: string;
  readonly testId: string;
  readonly onClose: () => void;
  /** The "+" button to anchor to (unused inside the bottom sheet). */
  readonly anchor: HTMLElement | null;
  /** Rendered inside the bottom sheet instead of floating beside the button. */
  readonly sheet?: boolean;
  /** A press on the anchor closes the panel (an anchor that is not a toggle button, e.g. a tile). */
  readonly closeOnAnchorPress?: boolean;
  readonly children: ReactNode;
}

function Panel({ id, label, testId, onClose, anchor, sheet = false, closeOnAnchorPress = false, children }: PanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const ghostGuard = useGhostClickGuard();

  const place = useCallback((): void => {
    const element = ref.current;
    if (element === null || anchor === null) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(WIDTH_PX, window.innerWidth - 2 * EDGE_PX);
    element.style.width = `${width}px`;
    const height = element.getBoundingClientRect().height;
    const left = Math.min(Math.max(EDGE_PX, rect.right - width), window.innerWidth - width - EDGE_PX);
    const below = rect.bottom + OFFSET_PX;
    const fitsBelow = below + height <= window.innerHeight - EDGE_PX;
    const above = rect.top - OFFSET_PX - height;
    // Flip upward only when the space above is the roomier one; else stay below and let it scroll.
    const top = fitsBelow || above < EDGE_PX ? below : above;
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.maxHeight = `${Math.max(MIN_PANEL_HEIGHT_PX, window.innerHeight - top - EDGE_PX)}px`;
    element.style.visibility = 'visible';
  }, [anchor]);

  useLayoutEffect(() => {
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [place]);

  // Passive effect: in the sheet the dialog opens in its own layout effect first.
  useEffect(() => {
    const element = ref.current;
    if (element !== null) (firstField(element) ?? element).focus({ preventScroll: true });
  }, []);

  // Listeners are subscribed once per anchor/mode, not once per new `onClose` of the parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      // Consumed in the capture phase: the picker dialog, the help overlay and quick-mark mode
      // listen further out and must not also react to this one key press.
      event.preventDefault();
      event.stopPropagation();
      onCloseRef.current();
    }
    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || ref.current?.contains(target) === true) return;
      if (!closeOnAnchorPress && anchor?.contains(target) === true) return; // the button toggles by itself
      onCloseRef.current();
    }
    document.addEventListener('keydown', onKeyDown, true);
    // The sheet closes through its own backdrop handler.
    if (!sheet) document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [anchor, sheet, closeOnAnchorPress]);

  const body = (
    <div
      ref={ref}
      id={id}
      className={cx(styles.panel, sheet && styles.inSheet)}
      role={sheet ? undefined : 'dialog'}
      aria-label={sheet ? undefined : label}
      tabIndex={-1}
      style={sheet ? undefined : { visibility: 'hidden' }}
      data-testid={`${testId}-panel`}
      onPointerDownCapture={sheet ? undefined : ghostGuard.onPointerDownCapture}
      onClickCapture={sheet ? undefined : ghostGuard.onClickCapture}
    >
      {children}
    </div>
  );
  return sheet ? body : createPortal(body, document.body);
}

interface AnchoredFormPopoverProps {
  readonly label: string;
  readonly testId: string;
  /** The element the panel hangs from (a quick-mark tile): the form opens in place, without a "+" button. */
  readonly anchor: HTMLElement;
  /** Esc, a press outside or the form's own completion. Focus returns to the anchor. */
  readonly onClose: () => void;
  readonly children: (close: () => void) => ReactNode;
}

/**
 * The popover of `AddPopover` opened from code on an arbitrary anchor (the quick-mark tiles'
 * "rename" form): same panel, placement, focus, Esc and bottom-sheet
 * behaviour, but no button of its own. Mounted means open.
 */
export function AnchoredFormPopover({ label, testId, anchor, onClose, children }: AnchoredFormPopoverProps) {
  const panelId = useId();
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  const close = useCallback((): void => {
    onClose();
    if (anchor.isConnected) anchor.focus({ preventScroll: true });
  }, [onClose, anchor]);
  return narrow ? (
    <Sheet label={label} onClose={close}>
      <Panel id={panelId} label={label} testId={testId} onClose={close} anchor={null} sheet>
        {children(close)}
      </Panel>
    </Sheet>
  ) : (
    <Panel id={panelId} label={label} testId={testId} onClose={close} anchor={anchor} closeOnAnchorPress>
      {children(close)}
    </Panel>
  );
}
